/**
 * PREVENTION-ACTION-CHECKLIST
 * Measures whose status and dates are edited through their wizard.
 */

import { NEWLY_ADDED_ACTION_PARAM, PreventionAction } from "../model/prevention-action.types"
import { filterMeasuresForView, PreventionMeasuresView } from "../model/prevention-action.utils"
import { PREVENTION_MEASURE_ICONS } from "../model/prevention-action.icons"
import { orderBy } from "lodash"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import PreventionActionChecklistItem from "./PreventionActionChecklistItem"
import PreventionActionCard from "./PreventionActionCard"
import getColor from "@/utils/get-color.utils"
import { useSearchParams } from "react-router-dom"
import { useEffect, useState } from "react"

interface OwnProps {
  measuresView?: PreventionMeasuresView
  isReadonly?: boolean
  onAdd?: () => void
  //Apres enregistrement, l'hote peut ouvrir l'evaluation avec le niveau suggere.
  //Sinon le wizard de la mesure gere cette evaluation.
  onReviewPrevention?: (suggestedPreventionValue: number) => void
  preventionActions: PreventionAction[]
}

export default function PreventionActionChecklist(props: OwnProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const addedActionParam = searchParams.get(NEWLY_ADDED_ACTION_PARAM)
  //Le repere est consomme des son affichage : son etat local laisse finir scroll et animation,
  //mais un rafraichissement de la page ne rejoue pas une ancienne creation.
  const [newlyAddedActionId, setNewlyAddedActionId] = useState(addedActionParam)
  const visibleActions = props.measuresView
    ? filterMeasuresForView(props.preventionActions, props.measuresView)
    : props.preventionActions
  const hasActions = props.preventionActions.length > 0
  const addAction =
    props.onAdd && !props.isReadonly ? (
      <PreventionActionCard
        control={
          <button
            type="button"
            aria-label="Ajouter une mesure"
            className="_hover flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[12px] p-0"
            style={{ backgroundColor: "#0E5AA7", border: "none", color: "white" }}
            onClick={(event) => {
              event.stopPropagation()
              props.onAdd?.()
            }}>
            <FontAwesomeIcon className="text-[18px]" icon={faPlus} />
          </button>
        }
        isDashed
        onClick={props.onAdd}>
        <span className="leading-tight">
          <b>Ajouter une mesure de prévention</b>
        </span>
        <span className="grey-t mt-1 text-sm leading-snug">
          Vous pouvez ajouter une ou plusieurs mesures de prévention pour le risque concerné.
        </span>
      </PreventionActionCard>
    ) : null

  useEffect(() => {
    if (props.isReadonly || !addedActionParam) return

    setNewlyAddedActionId(addedActionParam)
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete(NEWLY_ADDED_ACTION_PARAM)
    setSearchParams(nextSearchParams, { replace: true })
  }, [addedActionParam, props.isReadonly])

  return (
    <>
      {(addAction || hasActions) && (
        <div className="flex flex-col gap-[10px]">
          {addAction}
          {orderBy(
            visibleActions,
            [(action) => action.id === newlyAddedActionId, (action) => action.cStatus === "done", "datePlanned"],
            ["desc", "asc", "asc"]
          ).map((action) => (
            <PreventionActionChecklistItem
              key={action.id}
              action={action}
              isNewlyAdded={action.id === newlyAddedActionId}
              isReadonly={props.isReadonly}
              onReviewPrevention={props.onReviewPrevention}
            />
          ))}
        </div>
      )}
      {/* Meme carte que PreventionActionChecklistItem, en rouge clair : l'absence de mesure est une alerte */}
      {!hasActions && !addAction && (
        <div
          className="red-t flex min-h-[46px] items-center gap-[14px] rounded-[12px] px-[16px] py-[10px]"
          style={{ backgroundColor: `${getColor("red")}1A`, border: "1px solid #CBD5E1" }}>
          <FontAwesomeIcon icon={PREVENTION_MEASURE_ICONS.missing} />
          Aucune mesure de prévention renseignée.
        </div>
      )}
    </>
  )
}
